interface OAuthButtonProps {
  provider: 'google' | 'linkedin' | 'facebook' | 'apple';
  label: string;
  comingSoon?: boolean;
}

/**
 * Official brand marks for the OAuth providers. Brand logos must keep their
 * brand colours (hex is intentional here — not a theme token), except Apple,
 * which is a monochrome mark and inherits the button's text colour so it reads
 * correctly on the dark auth surface and dims with the `comingSoon` state.
 */
function ProviderIcon({ provider }: { provider: OAuthButtonProps['provider'] }) {
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', 'aria-hidden': true, focusable: false } as const;

  switch (provider) {
    case 'google':
      return (
        <svg {...common}>
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
          />
          <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
          />
        </svg>
      );
    case 'facebook':
      return (
        <svg {...common}>
          <path
            fill="#1877F2"
            d="M24 12c0-6.63-5.37-12-12-12S0 5.37 0 12c0 5.99 4.39 10.95 10.13 11.85v-8.38H7.08V12h3.05V9.36c0-3.01 1.79-4.67 4.53-4.67 1.31 0 2.69.23 2.69.23v2.96h-1.52c-1.49 0-1.96.93-1.96 1.88V12h3.33l-.53 3.47h-2.8v8.38C19.61 22.95 24 17.99 24 12z"
          />
        </svg>
      );
    case 'linkedin':
      return (
        <svg {...common}>
          <path
            fill="#0A66C2"
            d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.55C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.72C24 .77 23.2 0 22.22 0z"
          />
        </svg>
      );
    case 'apple':
      return (
        <svg {...common}>
          <path
            fill="currentColor"
            d="M17.05 12.04c-.03-2.86 2.34-4.23 2.44-4.3-1.33-1.95-3.4-2.21-4.14-2.24-1.76-.18-3.44 1.04-4.34 1.04-.9 0-2.28-1.02-3.75-.99-1.93.03-3.71 1.12-4.7 2.85-2 3.48-.51 8.63 1.44 11.46.95 1.38 2.09 2.94 3.58 2.88 1.44-.06 1.98-.93 3.72-.93 1.73 0 2.22.93 3.74.9 1.54-.03 2.52-1.41 3.46-2.8 1.09-1.6 1.54-3.15 1.57-3.23-.03-.02-3.02-1.16-3.05-4.6zM14.2 3.79c.8-.97 1.34-2.31 1.19-3.65-1.15.05-2.54.77-3.36 1.73-.74.86-1.38 2.23-1.21 3.55 1.28.1 2.58-.65 3.38-1.63z"
          />
        </svg>
      );
  }
}

export function OAuthButton({ provider, label, comingSoon }: OAuthButtonProps) {
  const handleClick = () => {
    if (comingSoon) return;
    const base = ((import.meta.env.VITE_API_URL as string) || '').replace(/\/$/, '');
    window.location.assign(`${base}/auth/${provider}`);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={comingSoon}
      title={comingSoon ? 'Coming soon' : `Continue with ${label}`}
      className={
        'inline-flex items-center justify-center gap-2 rounded-md border border-[var(--color-border-default)] px-3 py-2 text-sm font-medium transition-colors ' +
        (comingSoon
          ? 'cursor-not-allowed text-[var(--color-fg-subtle)] opacity-60'
          : 'text-[var(--color-fg-primary)] hover:bg-[var(--color-bg-surface)]')
      }
    >
      <ProviderIcon provider={provider} />
      <span>{label}</span>
      {comingSoon && (
        <span className="ml-1 rounded-full bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-[10px]">
          soon
        </span>
      )}
    </button>
  );
}
